import { Button, Form, Modal, Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useRef, useState } from "react";
import { ClaimFileDto } from "../types";
import { validateIfFilesAlreadyExistInClaim } from "../api/claimFollowUpAPI";
import AlertMessage from "../../../components/AlertMessage";
import { KeyValuePair } from "../../../types/KeyValuePair";

type ClaimAttachDocumentsModalProps = {
    claimId: number;
    show: boolean;
    possibleClaimFiles: ClaimFileDto[];
    onClose: () => void;
    onSubmit: (data: { selectedClaimFiles?: ClaimFileDto[]; addedFiles?: File[] }) => void;
};

export default function ClaimAttachDocumentsModal({
    claimId,
    show,
    possibleClaimFiles,
    onClose,
    onSubmit,
}: ClaimAttachDocumentsModalProps) {
    const { t } = useTranslation(["claim", "common"]);
    const [addedFiles, setAddedFiles] = useState<File[]>([]);
    const [selectedClaimFiles, setSelectedClaimFiles] = useState<ClaimFileDto[]>([]);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [duplicatedFiles, setDuplicatedFiles] = useState<KeyValuePair[]>([]);
    const handleCheckboxChange = (file: ClaimFileDto, checked: boolean) => {
        setSelectedClaimFiles((prev) =>
            checked
                ? [...prev, file]
                : prev.filter((f) => f.id !== file.id)
        );
    };

    const handleAttachClick = () => {
        fileInputRef.current?.click();
    };

    const handleFilesSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (files && files.length > 0) {
            setAddedFiles((prev) => [...prev, ...Array.from(files)]);
        }
    };

    const handleRemoveAddedFile = (index: number) => {
        setAddedFiles((prev) => prev.filter((_, i) => i !== index));
    };

    const handleSubmit = async () => {
        setDuplicatedFiles([]);

        let duplicatedFiles: KeyValuePair[] = [];
        if (addedFiles)
            duplicatedFiles = await validateIfFilesAlreadyExistInClaim(claimId, addedFiles);
        
        if (duplicatedFiles.length === 0) {
            onSubmit({ selectedClaimFiles, addedFiles });
            onClose();
        } else {
            setDuplicatedFiles(duplicatedFiles);
        }
    };

    const isFileDuplicated = (file: File) =>
        duplicatedFiles?.some(duplicatedFile => duplicatedFile.key === file.name && duplicatedFile.value === file.size);
    
    return (
        <Modal show={show} onHide={onClose} centered className="improvement-plan-finalize-modal" size="lg">
            <Modal.Header closeButton></Modal.Header>
            <Modal.Body>
                {duplicatedFiles.length > 0 &&
                    <AlertMessage
                        message={"Se está intentado subir documentos duplicados"}
                        type="danger"
                    />
                }
                <Row>
                    <Col xs={12}>
                        <span>{t("common:attachDocuments.selectFiles")}</span>
                        {possibleClaimFiles.map((claimFile) => (
                            <div key={claimFile.id}>
                                <Form.Check
                                    type="checkbox"
                                    id={`claim-file-${claimFile.id}`}
                                    label={claimFile.fileName}
                                    checked={selectedClaimFiles.some((f) => f.id === claimFile.id)}
                                    onChange={(e) => handleCheckboxChange(claimFile, e.target.checked)}
                                />
                            </div>
                        ))}
                        {addedFiles.length > 0 && (
                            <>
                                {addedFiles.map((file, index) => (
                                    <div key={`${file.name}-${index}`}
                                        className={`d-flex align-items-center ${(isFileDuplicated(file) ? "text-danger" : "")}`}
                                    >
                                        {file.name}
                                        <button
                                            type="button"
                                            className="btn btn-link p-0 ms-2 text-secondary"
                                            onClick={() => handleRemoveAddedFile(index)}
                                            style={{ textDecoration: "none" }}
                                        >
                                            x
                                        </button>
                                    </div>
                                ))}
                            </>
                        )}
                        {possibleClaimFiles.length === 0 && addedFiles.length == 0 && (
                            <div className="text-muted">
                                {t("common:attachDocuments.noFiles")}
                            </div>
                        )}
                    </Col>                            
                </Row>
                <Row className="mt-3">
                    <Col xs={12}>
                        <div className="d-flex align-items-center mb-3">
                            <Button variant="outline-success" onClick={handleAttachClick}>
                                {t("common:attachDocuments.attachNew")}
                            </Button>
                            <Form.Control
                                ref={fileInputRef}
                                type="file"
                                multiple
                                className="d-none"
                                onChange={handleFilesSelected}
                            />
                        </div>
                    </Col>
                </Row>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onClose}>
                    {t("common:buttons.cancel")}
                </Button>
                <Button variant="primary" onClick={handleSubmit}>
                    {t("common:buttons.accept")}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}